// /app/frontend/src/components/InnerWorkModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { createReflection } from "../api/innerWork";

const FEELINGS = [
  "grounded",
  "hopeful",
  "calm",
  "curious",
  "excited",
  "confused",
  "anxious",
  "triggered",
  "angry",
  "loved",
];

export default function InnerWorkModal({
  open,
  onClose,
  prompt,
  onCreated, // (reflection) => void
  title = "Inner Work",
}) {
  const [text, setText] = useState("");
  const [feeling, setFeeling] = useState("");
  const [clarity, setClarity] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setText("");
    setFeeling("");
    setClarity("");
    setErr("");
    setLoading(false);
  }, [open]);

  const canSubmit = useMemo(() => text.trim().length > 0 && !loading, [text, loading]);

  const submit = async () => {
    if (!canSubmit) return;
    setErr("");
    setLoading(true);
    try {
      const payload = {
        prompt_id: prompt?.id || null,
        reflection_text: text.trim(),
        feeling_tag: feeling || null,
        clarity_score: clarity ? Number(clarity) : null,
      };
      const created = await createReflection(payload);
      onCreated?.(created);
      onClose?.();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Could not save reflection.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-slate-900 border border-slate-800 p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-white">{title}</div>
            <div className="text-xs text-slate-400">Private by default. Share only if you choose.</div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {prompt?.prompt_text ? (
          <div className="mt-4 rounded-xl bg-slate-800/50 border border-slate-700 p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Today&apos;s Reflection</div>
            <div className="mt-2 text-sm text-slate-200">{prompt.prompt_text}</div>
          </div>
        ) : null}

        <div className="mt-4">
          <textarea
            className="h-32 w-full resize-none rounded-xl border border-slate-700 bg-slate-800/50 p-4 text-sm text-white placeholder-slate-500 outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20 transition"
            placeholder="Write what&apos;s real. One paragraph is enough."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-slate-400 mb-2">Feeling (optional)</div>
            <select
              className="w-full rounded-xl border border-slate-700 bg-slate-800/50 p-3 text-sm text-white outline-none focus:border-rose-500/50 transition"
              value={feeling}
              onChange={(e) => setFeeling(e.target.value)}
            >
              <option value="">—</option>
              {FEELINGS.map((f) => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-2">Clarity (optional)</div>
            <select
              className="w-full rounded-xl border border-slate-700 bg-slate-800/50 p-3 text-sm text-white outline-none focus:border-rose-500/50 transition"
              value={clarity}
              onChange={(e) => setClarity(e.target.value)}
            >
              <option value="">—</option>
              <option value="1">1 (foggy)</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5 (clear)</option>
            </select>
          </div>
        </div>

        {err ? <div className="mt-3 text-sm text-red-400">{err}</div> : null}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            className="rounded-xl border border-slate-700 px-5 py-2.5 text-sm text-slate-300 hover:bg-slate-800 transition"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-rose-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50 transition"
            onClick={submit}
            disabled={!canSubmit}
          >
            {loading ? "Saving…" : "Save reflection"}
          </button>
        </div>
      </div>
    </div>
  );
}
